import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { AlertTriangle, Crosshair, Rocket, Users } from 'lucide-react';
// Kit Modal (not the raw Mantine one) — it themes content/header/overlay from
// the --c-* tokens, which the raw Modal misses entirely.
import { Badge, Button, Modal, Pill } from '../ui';
import { colors } from '../../theme/tokens';
import { useCohorts } from '../../hooks/useCohorts';
import { useUsers } from '../../hooks/useAdmin';
import { useDeployScenario } from '../../hooks/useScenarios';
import { toast } from '../../store/useToasts';
import type { ScenarioDetail } from '../../api/types';

/**
 * DeployRangeModal — team trainee picker + deploy submitter.
 *
 * Operator decision 2026-05-25: 1 range is shared by N trainees acting as
 * a SOC team. The modal lets the instructor:
 *   1. Optionally filter trainees by cohort (or run ad-hoc team selection)
 *   2. Multi-select the trainees on this team
 *   3. See live cost preview (depends on workstation count = team size)
 *   4. Deploy → backend fans out workstation count to len(trainee_ids).
 *
 * Backend wiring lives in services/deploy/handler.py#cmd_deploy_range, which
 * accepts `trainee_ids: string[]` + `cohort_id: string | null` and tags each
 * windows-workstation replica with forge:assigned_to=<user_id>.
 */

const INSTANCE_COST_HR: Record<string, number> = {
  't3.small': 0.021,
  't3.medium': 0.042,
  't3.large': 0.083,
  't3.xlarge': 0.166,
  'm5.large': 0.096,
  'm5.xlarge': 0.192,
};

interface DeployRangeModalProps {
  opened: boolean;
  onClose: () => void;
  scenario: ScenarioDetail | null;
  /** Optional callback after successful deploy. Receives the new range id. */
  onDeployed?: (rangeId: string) => void;
}

export function DeployRangeModal({
  opened,
  onClose,
  scenario,
  onDeployed,
}: DeployRangeModalProps) {
  const [rangeName, setRangeName] = useState('');
  const [cohortId, setCohortId] = useState<string | null>(null);
  const [traineeIds, setTraineeIds] = useState<ReadonlyArray<string>>([]);

  const cohortsQ = useCohorts();
  const usersQ = useUsers();
  const deployM = useDeployScenario();

  // Trainee candidates = all users with role trainee or instructor (the
  // instructor can join their own training session for demo / debug).
  // When a cohort is selected we filter further to its members - but
  // CohortSummary doesn't expose a member list yet, so for v1 we just
  // show all trainees and the operator picks manually. Cohort filter is
  // metadata-only; backend stores cohort_id alongside trainee_ids[].
  const traineeCandidates = useMemo(() => {
    const users = usersQ.data ?? [];
    return users.filter((u) => u.role === 'trainee' || u.role === 'instructor');
  }, [usersQ.data]);

  // ---- cost preview ----------------------------------------------------
  const cost = useMemo(() => {
    if (!scenario) return 0;
    let total = 0;
    for (const vm of scenario.vms ?? []) {
      const perInstance = INSTANCE_COST_HR[vm.instanceType ?? ''] ?? 0.05;
      // If this VM is a windows-workstation, count = team size (override).
      const isWorkstation = vm.role === 'windows-workstation' || vm.role === 'base-windows-10';
      const effectiveCount = isWorkstation
        ? Math.max(1, traineeIds.length || (vm.count ?? 1))
        : vm.count ?? 1;
      total += perInstance * effectiveCount;
    }
    return total;
  }, [scenario, traineeIds]);

  // ---- workstation count preview --------------------------------------
  const workstationVms = useMemo(
    () =>
      (scenario?.vms ?? []).filter(
        (vm) => vm.role === 'windows-workstation' || vm.role === 'base-windows-10',
      ),
    [scenario],
  );

  const toggleTrainee = (id: string) => {
    setTraineeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleDeploy = () => {
    if (!scenario) return;
    const payload: Parameters<typeof deployM.mutate>[0] = {
      scenarioSlug: scenario.slug,
      traineeIds,
    };
    if (cohortId) payload.cohortId = cohortId;
    const trimmedName = rangeName.trim();
    if (trimmedName) payload.name = trimmedName;
    deployM.mutate(
      payload,
      {
        onSuccess: (resp) => {
          toast.success('Range deploying', `${scenario.name} — provisioning has started.`);
          onDeployed?.(resp.rangeId);
          onClose();
          setTraineeIds([]);
          setCohortId(null);
          setRangeName('');
        },
      },
    );
  };

  const cohortOptions = useMemo(
    () => (cohortsQ.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [cohortsQ.data],
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Rocket size={16} color={colors.neon} />
          <Text fw={700}>
            Deploy range — {scenario?.name ?? '...'}
          </Text>
        </Group>
      }
      size="xl"
      centered
    >
      <Stack gap="md">
        <TextInput
          label="Range name (optional)"
          placeholder={scenario ? `Range-${scenario.slug}` : 'Range name'}
          value={rangeName}
          onChange={(e) => setRangeName(e.currentTarget.value)}
          size="sm"
        />

        <Box>
          <Text fw={600} size="sm" mb={4}>
            <Group gap="xs" wrap="nowrap">
              <Users size={14} color={colors.hudCyan} />
              <span>Team — pick which trainees join this range</span>
            </Group>
          </Text>
          <Stack gap="sm">
            <Select
              label="Cohort (optional)"
              placeholder="Ad-hoc team (no cohort)"
              value={cohortId}
              onChange={setCohortId}
              data={cohortOptions}
              clearable
              size="sm"
            />

            <Box
              p="sm"
              style={{
                background: 'rgb(var(--c-panel))',
                border: '1px solid var(--c-bd)',
                borderRadius: 8,
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {usersQ.isLoading ? (
                <Text size="sm" c="dimmed">
                  Loading trainees...
                </Text>
              ) : traineeCandidates.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No trainees in this tenant yet. Invite users from /admin/users first, or
                  deploy without a team (creates a range with default workstation count).
                </Text>
              ) : (
                <Stack gap={6}>
                  {traineeCandidates.map((u) => (
                    <Checkbox
                      key={u.id}
                      checked={traineeIds.includes(u.id)}
                      onChange={() => toggleTrainee(u.id)}
                      label={
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm" ff="JetBrains Mono">
                            {u.email}
                          </Text>
                          <Pill variant={u.role === 'trainee' ? 'neutral' : 'warn'} size="xs">
                            {u.role}
                          </Pill>
                          {u.status !== 'active' && (
                            <Pill variant="warn" size="xs">
                              {u.status}
                            </Pill>
                          )}
                        </Group>
                      }
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Group gap="xs">
              <Badge variant="info" size="xs">
                {traineeIds.length} selected
              </Badge>
              <Badge variant="neutral" size="xs">
                {workstationVms.length} workstation role(s) in scenario
              </Badge>
              {traineeIds.length > 0 && workstationVms.length > 0 && (
                <Badge variant="success" size="xs">
                  Each trainee gets their own workstation
                </Badge>
              )}
            </Group>
          </Stack>
        </Box>

        <Box
          p="sm"
          style={{
            background: 'rgb(var(--c-panel))',
            border: '1px solid var(--c-bd)',
            borderRadius: 8,
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Crosshair size={14} color={colors.hudAmber} />
              <Text size="sm" fw={600}>
                Live cost preview
              </Text>
            </Group>
            <Text ff="JetBrains Mono" fw={700} size="lg" c={colors.neon}>
              ${cost.toFixed(2)}/hr
            </Text>
          </Group>
          <Text size="xs" c="dimmed" mt={4}>
            4-hour auto-destroy ⇒ ~${(cost * 4).toFixed(2)} per training session.
            Workstations: {traineeIds.length || workstationVms.reduce((s, v) => s + (v.count ?? 1), 0)} ×
            {' '}{workstationVms[0]?.instanceType ?? 't3.large'}.
          </Text>
        </Box>

        {deployM.isError && (
          <Alert color="red" variant="light" icon={<AlertTriangle size={14} />}>
            {deployM.error?.message ?? 'Deploy failed — see console for details.'}
          </Alert>
        )}

        {traineeIds.length === 0 && (
          <Alert color="cyan" variant="light" icon={<AlertTriangle size={14} />}>
            No trainees selected. The range will deploy with the scenario's default workstation
            count ({workstationVms.reduce((s, v) => s + (v.count ?? 1), 0)}) and no
            forge:assigned_to tags. Pick at least one trainee to enable per-trainee workstation
            assignment.
          </Alert>
        )}

        <Group justify="space-between">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Rocket size={14} />}
            onClick={handleDeploy}
            disabled={deployM.isPending || !scenario}
          >
            {deployM.isPending ? 'Deploying...' : `Deploy range${traineeIds.length ? ` for ${traineeIds.length} trainee${traineeIds.length === 1 ? '' : 's'}` : ''}`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
